/* eslint-disable no-underscore-dangle */
import React from 'react';
import { GetStaticProps } from 'next';
import Head from 'next/head';
import { Flex } from '@chakra-ui/react';
import sdk from '@src/lib/sdk';
import Navigation from '@components/Shared/Navigation/Navigation';
import { ProjectConfigQuery } from '@src/generated/graphql';
import MyProjects from '@components/MyProjects/MyProjects';
import SectionHeader from '@components/Shared/SectionHeader';
import { DeepExtractTypeSkipArrays } from '@src/types/extract-types';
import ImageGallery from '@components/ImageGallery/ImageGallery';
import { NavigationDetails } from '@components/Shared/InnerNavigation/interfaces';
import InnerNavigation from '@components/Shared/InnerNavigation/InnerNavigation';

export interface InitData {
  project: DeepExtractTypeSkipArrays<
    ProjectConfigQuery,
    ['allMyProjectsConfig', 'navigation', 'link']
  >;
  navDetails: NavigationDetails[];
}

export async function getStaticPaths() {
  const {
    allMyProjectsConfig: [projectConfig],
  } = await sdk.ProjectsSlugs();
  return {
    paths:
      projectConfig.navigation?.map((project) => ({
        params: {
          slug: project?.link?.slug?.current,
        },
      })) || [],
    fallback: true,
  };
}

export const getStaticProps: GetStaticProps = async ({ params }) => {
  if (typeof params?.slug !== 'string') return Promise.resolve({ props: {} });
  const {
    allMyProjectsConfig: [projectConfig],
  } = await sdk.ProjectConfig();

  const navDetails = projectConfig?.navigation?.map((project) => ({
    slug: project?.link?.slug,
    title: project?.link?.title,
  }));

  return {
    props: {
      project: projectConfig.navigation?.find(
        (nav) => nav?.link?.slug?.current === params.slug
      )?.link,
      navDetails,
    },
  };
};

const Projects: React.FC<InitData> = ({ project, navDetails }) => {
  if (!project) {
    return null;
  }
  return (
    <Flex direction="column">
      <Head>
        <title>Ohsofree - Projects</title>
      </Head>
      <Navigation />
      <Flex mt="80px" mb="18px" justify="center">
        <SectionHeader normalText="My" boldText="Projects" />
      </Flex>
      <InnerNavigation navDetails={navDetails} />
      {project.__typename === 'MyProjects' ? (
        <MyProjects project={project} />
      ) : (
        <ImageGallery {...project} />
      )}
    </Flex>
  );
};

export default Projects;
